import { getReleaseUiName, useRecentReleases } from "@/features/release";
import {
  getStoredReleaseFilterLabel,
  getStoredReleaseFilterValue,
  setStoredReleaseFilterValue,
} from "@/features/releaseFilter/releaseFilterStorage";
import { useScope } from "@/redux/scope";
import Select, { type SelectOption } from "@imminently/decisively-ui/Select";
import { useCallback, useEffect, useMemo, useState } from "react";

export const useReleaseFilter = (filterKey?: string | null) => {
  const { project } = useScope();
  const [releaseId, setReleaseIdState] = useState<string | null>(() =>
    filterKey ? getStoredReleaseFilterValue(filterKey, project) : null,
  );
  const [releaseLabel, setReleaseLabelState] = useState<string | null>(() =>
    filterKey ? getStoredReleaseFilterLabel(filterKey, project) : null,
  );

  useEffect(() => {
    setReleaseIdState(filterKey ? getStoredReleaseFilterValue(filterKey, project) : null);
    setReleaseLabelState(filterKey ? getStoredReleaseFilterLabel(filterKey, project) : null);
  }, [filterKey, project]);

  const setReleaseId = useCallback(
    (nextReleaseId: string | null, nextReleaseLabel?: string | null) => {
      setReleaseIdState(nextReleaseId);
      setReleaseLabelState(nextReleaseLabel ?? null);
      if (filterKey) setStoredReleaseFilterValue(filterKey, project, nextReleaseId, nextReleaseLabel);
    },
    [filterKey, project],
  );

  return { releaseId, releaseLabel, setReleaseId };
};

export interface ReleaseFilterSelectProps {
  filterKey: string;
  releaseId?: string | null;
  className?: string;
  onReleaseChange?: (releaseId: string | null, releaseLabel?: string | null) => void;
}

export const ReleaseFilterSelect = ({
  filterKey,
  releaseId: controlledReleaseId,
  className,
  onReleaseChange,
}: ReleaseFilterSelectProps) => {
  const { project } = useScope();
  const { releaseId: storedReleaseId, releaseLabel: storedReleaseLabel, setReleaseId } = useReleaseFilter(filterKey);
  const [hasOpened, setHasOpened] = useState(false);
  const isControlled = controlledReleaseId !== undefined;
  const releaseId = controlledReleaseId === undefined ? storedReleaseId : controlledReleaseId;
  const { releases: recentReleases, query } = useRecentReleases(project, "all", hasOpened);

  const options = useMemo<SelectOption[]>(() => {
    const releaseOptions = (recentReleases ?? []).map((release) => ({
      value: release.id,
      label: getReleaseUiName(release),
    }));

    if (releaseId && storedReleaseLabel && !releaseOptions.some((option) => option.value === releaseId)) {
      return [{ value: releaseId, label: storedReleaseLabel }, ...releaseOptions];
    }

    return releaseOptions;
  }, [recentReleases, releaseId, storedReleaseLabel]);

  const handleChange = useCallback(
    (nextReleaseId: string | null) => {
      const nextReleaseLabel = options.find((option) => option.value === nextReleaseId)?.label;
      if (!isControlled) setReleaseId(nextReleaseId, nextReleaseLabel);
      onReleaseChange?.(nextReleaseId, nextReleaseLabel);
    },
    [isControlled, onReleaseChange, options, setReleaseId],
  );

  return (
    <Select
      clearable
      value={releaseId}
      onValueChange={handleChange}
      onMenuOpen={() => setHasOpened(true)}
      options={options}
      isLoading={query.isFetching}
      placeholder="Release"
      className={className}
    />
  );
};
